import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import '../styles/ArchitecturalConstruction.css';

const ArchitecturalConstruction = () => {
  const [activeTab, setActiveTab] = useState('gioi-thieu');
  const [step, setStep] = useState(1);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  // Fetch projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching architecture projects...');
        
        // Try to fetch projects by category first
        let response = await fetch(`${API_BASE_URL}/api/projects/category/architecture?limit=6`);
        
        if (!response.ok) {
          // If category endpoint fails, try featured projects
          response = await fetch(`${API_BASE_URL}/api/projects/featured?limit=6`);
        }

        if (response.ok) {
          const data = await response.json();
          const processedProjects = data.data?.map(processProject) || [];
          setProjects(processedProjects);
          console.log('✅ Projects loaded:', processedProjects.length);
        } else {
          throw new Error('Failed to fetch projects');
        }
        
      } catch (err) {
        console.error('❌ Error fetching projects:', err);
        setError(err.message);
        
        // Use fallback data if API fails
        setProjects(getFallbackProjects());
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [API_BASE_URL]);

  // Process project data from API
  const processProject = (project) => ({
    id: project.id,
    title: project.title,
    description: project.description || `Dự án ${project.title} - Thiết kế và thi công chuyên nghiệp`,
    image: getImageUrl(project.featured_image),
    technologies: project.technologies ? (typeof project.technologies === 'string' ? JSON.parse(project.technologies) : project.technologies) : ['Kiến trúc', 'Xây dựng'],
    status: project.status || 'completed',
    featured: project.is_featured || true,
    slug: project.slug,
    client: project.client_name,
    views: project.views_count || 0
  });

  // Get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/images/arch-villa-1.jpg';
    if (imagePath.startsWith('/uploads/')) return `${API_BASE_URL}${imagePath}`;
    return imagePath;
  };

  // Fallback projects data
  const getFallbackProjects = () => [
    {
      id: 1,
      title: 'Biệt thự cao cấp Saigon Pearl',
      description: 'Thiết kế và thi công biệt thự 4 tầng, diện tích 500m², phong cách hiện đại kết hợp cổ điển.',
      image: '/images/arch-villa-1.jpg',
      technologies: ['Modern Architecture', 'Luxury Design'],
      status: 'completed',
      featured: true
    },
    {
      id: 2,
      title: 'Tòa nhà văn phòng Diamond Plaza',
      description: 'Tòa nhà 20 tầng tại trung tâm TP.HCM, diện tích cho thuê 15,000m², đạt chứng nhận LEED.',
      image: '/images/arch-office-1.jpg',
      technologies: ['Commercial Building', 'LEED Certified'],
      status: 'completed',
      featured: true
    },
    {
      id: 3,
      title: 'Nhà máy Honda Việt Nam',
      description: 'Nhà máy sản xuất xe máy quy mô 50,000m², ứng dụng công nghệ tự động hóa hiện đại.',
      image: '/images/arch-factory-1.jpg',
      technologies: ['Industrial Construction', 'Automation'],
      status: 'completed',
      featured: true
    },
    {
      id: 4,
      title: 'Bệnh viện đa khoa FV',
      description: 'Bệnh viện 15 tầng với 300 giường bệnh, trang bị hệ thống y tế tiên tiến nhất.',
      image: '/images/arch-hospital-1.jpg',
      technologies: ['Healthcare Architecture', 'Medical Systems'],
      status: 'completed',
      featured: true
    }
  ];

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  // Scroll to specific section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle "TÌM HIỂU THÊM" button clicks
  const handleLearnMore = (type) => {
    scrollToSection('advantages-section');
  };

  const renderGioiThieu = () => (
    <div className="arch-intro-content">
      <h2 className="section-title">Giải pháp cho kiến trúc & xây dựng</h2>
      <div className="arch-grid">
        {[
          { 
            title: 'Thiết kế kiến trúc', 
            subtitle: 'Architectural Design',
            desc: 'Thiết kế tổng thể cho công trình như nhà ở, văn phòng, chung cư, công trình công cộng,... Đảm bảo sự hài hòa giữa thẩm mỹ, công năng và tính bền vững.', 
            img: '/images/Rectangle-15.png' 
          },
          { 
            title: 'Thiết kế kết cấu', 
            subtitle: 'Structural Design',
            desc: 'Tập trung vào phần kết cấu chịu lực của công trình như móng, cột, dầm, sàn, tường chịu lực,... Đảm bảo an toàn, chịu lực tốt theo các tiêu chuẩn xây dựng.', 
            img: '/images/Rectangle-15-1.png' 
          },
          { 
            title: 'Thiết kế hệ thống cơ điện', 
            subtitle: 'MEP Design - Mechanical, Electrical, Plumbing',
            desc: 'Hệ thống điện, cấp thoát nước, điều hòa không khí, phòng cháy chữa cháy, hệ thống thông gió,... Đảm bảo công trình vận hành an toàn, hiệu quả.', 
            img: '/images/Rectangle-15-2.png' 
          },
          { 
            title: 'Thi công và hoàn thiện', 
            subtitle: 'Construction & Finishing',
            desc: 'Triển khai thi công theo bản vẽ thiết kế. Đảm bảo tiến độ, chất lượng công trình và tuân thủ các tiêu chuẩn kỹ thuật.', 
            img: '/images/Rectangle-15-3.png' 
          }
        ].map((item, idx) => (
          <div key={idx} className="arch-card">
            <img src={item.img} alt={item.title} className="arch-card-image" />
            <div className="arch-card-content">
              <h3>
                {item.title}
                <br />
                <span className="subtitle">{item.subtitle}</span>
              </h3>
              <p>{item.desc}</p>
              <button className="learn-more-btn" onClick={() => handleLearnMore('architectural-design')}>TÌM HIỂU THÊM</button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Ưu điểm của giải pháp */}
      <div id="advantages-section" className="advantages-section">
        <h2 className="section-title">Ưu điểm của giải pháp</h2>
        <div className="advantages-content">
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>Thiết kế chuyên nghiệp</h3>
              <p>Các bên liên quan trực tiếp trải nghiệm không gian trước khi thi công. Đồng thời, các bản render chân thực và bảng vật liệu mẫu (Material Board) tạo điều kiện để đánh giá và lựa chọn hoàn thiện về màu sắc, ánh sáng, kết cấu bề mặt, đảm bảo tính nhất quán giữa ý tưởng và hiện thực.<br/><br/>Cuối cùng, hệ thống tài liệu thiết kế (Design Documentation) chuẩn hóa theo ISO và các tiêu chuẩn quốc tế không chỉ giúp đẩy nhanh quy trình phê duyệt mà còn thuận tiện cho việc bảo trì, nâng cấp theo nhu cầu phát triển trong tương lai.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle29.png" alt="Thiết kế chuyên nghiệp" />
            </div>
          </div>
          
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>Quản lý dự án</h3>
              <p>Quy trình quản lý chặt chẽ giúp theo dõi, kiểm soát mọi khía cạnh của dự án, từ thiết kế đến thi công. Điều này giảm thiểu rủi ro, phát hiện sớm vấn đề và đảm bảo việc sử dụng tài nguyên hiệu quả. Quản lý dự án cũng tạo ra sự minh bạch, cải thiện giao tiếp giữa các bên, giúp công trình hoàn thành đạt hiệu quả cao về chất lượng và tiến độ.<br/><br/>Bằng cách xây dựng kế hoạch chi tiết với các mốc thời gian (milestone) và ngân sách được phân bổ rõ ràng ngay từ giai đoạn khởi động từ đó đảm bảo mọi thay đổi đều được ghi nhận, đánh giá và phê duyệt kịp thời.<br/><br/>Các buổi họp định kỳ kết hợp báo cáo KPI và dashboard trực quan giúp giám sát sát sao tiến độ, chi phí và chất lượng so với kế hoạch ban đầu.<br/><br/>Nhờ đó, dự án không chỉ đạt được hiệu quả cao về mặt chi phí – tiến độ – chất lượng, mà còn tạo dựng uy tín cho chủ đầu tư thông qua việc bàn giao công trình đúng cam kết, kèm bộ hồ sơ hoàn công, bảo trì rõ ràng và dễ theo dõi.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle30.png" alt="Quản lý dự án" />
            </div>
          </div>
          
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>Thi công chất lượng cao</h3>
              <p>Thi công chất lượng đảm bảo công trình hoàn thiện đúng tiến độ và tiêu chuẩn kỹ thuật, với độ bền cao và tính an toàn vượt trội. Quy trình thi công được kiểm soát chặt chẽ, giúp giảm thiểu sai sót và chi phí sửa chữa. Sử dụng vật liệu đạt chuẩn và đội ngũ thợ lành nghề đảm bảo hiệu quả lâu dài.<br/><br/>Với thi công chất lượng cao, mọi giai đoạn từ chuẩn bị mặt bằng, lắp dựng kết cấu đến hoàn thiện nội–ngoại thất đều được giám sát theo quy trình quản lý dự án chuyên nghiệp, bảo đảm tính đồng bộ và linh hoạt khi có phát sinh. Ứng dụng công nghệ BIM và hệ thống kiểm thử vật liệu tiên tiến giúp tối ưu hóa nguồn lực, rút ngắn thời gian thi công mà vẫn duy trì mức sai số thấp nhất. Đội ngũ kỹ sư và thợ lành nghề được đào tạo bài bản, tuân thủ nghiêm ngặt tiêu chuẩn QA/QC, không chỉ nâng cao chất lượng bề mặt và độ kín khít các mối nối mà còn đảm bảo công trình vận hành an toàn, ổn định trong suốt vòng đời.<br/><br/>Bên cạnh đó, việc ưu tiên sử dụng vật liệu xanh, tiết kiệm năng lượng và quy trình thi công ít phát thải góp phần gia tăng giá trị bền vững, tạo dựng hình ảnh thương hiệu chủ đầu tư uy tín và đẳng cấp.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle31.png" alt="Thi công chất lượng cao" />
            </div>
          </div>
          
          <div className="advantage-item">
            <div className="advantage-text">
              <h3>Vật liệu cao cấp</h3>
              <p>Vật liệu cao cấp giúp nâng tầm chất lượng và độ bền của công trình, chống chịu tốt với thời tiết và môi trường khắc nghiệt. Khả năng cách âm, cách nhiệt vượt trội mang lại không gian sống thoải mái, tiết kiệm năng lượng. Ngoài ra, tính thẩm mỹ của vật liệu cao cấp tạo nên vẻ đẹp sang trọng, hiện đại.<br/><br/>Việc sử dụng vật liệu chất lượng còn giúp giảm chi phí bảo trì, sửa chữa và gia tăng giá trị công trình theo thời gian.</p>
            </div>
            <div className="advantage-image">
              <img src="/images/Rectangle32.png" alt="Vật liệu cao cấp" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoiIch = () => (
    <div className="benefits-content">
      <div className="benefits-grid">
        {[
          { icon: '🏗️', title: 'Chất lượng vượt trội', desc: 'Sử dụng vật liệu cao cấp, công nghệ thi công tiên tiến, kiểm soát chất lượng nghiêm ngặt từng công đoạn.' },
          { icon: '⏰', title: 'Tiến độ đúng hẹn', desc: 'Quản lý dự án chuyên nghiệp, lập kế hoạch chi tiết, đảm bảo bàn giao đúng thời gian cam kết.' },
          { icon: '💰', title: 'Chi phí hợp lý', desc: 'Báo giá minh bạch, tối ưu hóa chi phí thi công, không phát sinh ngoài dự toán đã thỏa thuận.' },
          { icon: '🌱', title: 'Kiến trúc xanh', desc: 'Ứng dụng giải pháp kiến trúc xanh, tiết kiệm năng lượng, thân thiện với môi trường.' }
        ].map((item, idx) => (
          <div key={idx} className="benefit-card">
            <div className="benefit-header">
              <div className="benefit-icon">
                <span>{item.icon}</span>
              </div>
              <h3>{item.title}</h3>
            </div>
            <p>{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProjects = () => (
    <div className="projects-content">
      <h2 className="section-title">Dự án tiêu biểu</h2>
      
      {/* API Status Indicator */}
      {error && (
        <div className="api-status error">
          ⚠️ Đang sử dụng dữ liệu mẫu (API: {error})
        </div>
      )}
      
      {loading ? (
        <div className="loading-projects">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project) => (
            <div key={project.id} className="project-card">
              <img src={project.image} alt={project.title} className="project-image" />
              <div className="project-content">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-tech">
                  {project.technologies.slice(0, 3).map((tech, idx) => (
                    <span key={idx} className="tech-tag">{tech}</span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span className="tech-tag extra">+{project.technologies.length - 3}</span>
                  )}
                </div>
                <div className="project-footer">
                  <span className="project-status">
                    {project.status === 'completed' ? '✅ Hoàn thành' : 
                     project.status === 'in-progress' ? '🔄 Đang thực hiện' : '📋 Lên kế hoạch'}
                  </span>
                  <button onClick={() => handleViewDetails(project)} className="view-details-btn">
                    Xem chi tiết →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Video Section */}
      <div className="video-section">
        <h3 className="video-title">Video dự án tiêu biểu</h3>
        <div className="video-container">
          <div className="video-player">
            <iframe
              src="https://www.youtube.com/embed/LOLiq4w7SEs"
              title="Hệ thống điện mặt trời gia đình 5kW"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="video-iframe"
            />
          </div>
        </div>
      </div>
      
      {/* Partners Section */}
      <div className="partners-section">
        <h3 className="partners-title">Đối tác thương hiệu</h3>
        <div className="partners-grid">
          {[
            'Asset1.png', 'Asset3.png', 'Asset4.png', 'Asset5.png', 
            'Asset6.png', 'asset7.png'
          ].map((partner, idx) => (
            <div key={idx} className="partner-logo">
              <img src={`/images/${partner}`} alt={`Partner ${idx + 1}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'gioi-thieu': return renderGioiThieu();
      case 'loi-ich': return renderLoiIch();
      case 'quy-trinh': return (
        <div className="process-content">
          <h2 className="section-title">Quy trình thiết kế & thi công</h2>
          <p className="process-description">Quy trình thiết kế và thi công chuyên nghiệp từ khảo sát, thiết kế đến hoàn thiện và bàn giao, đảm bảo chất lượng và tiến độ tối ưu.</p>
          <div className="process-timeline">
            {[
              { title: 'BƯỚC 1: KHẢO SÁT & TƯ VẤN', icon: '/images/Untitled-1.png', content: 'Khảo sát thực địa, tìm hiểu nhu cầu khách hàng, phân tích điều kiện địa chất, hạ tầng. Tư vấn phương án thiết kế và báo giá sơ bộ.', image: '/images/Rectangle-15-3.png' },
              { title: 'BƯỚC 2: THIẾT KẾ SƠ BỘ', icon: '/images/Untitled-2.png', content: 'Lập thiết kế sơ bộ, bản vẽ 3D, phối cảnh kiến trúc. Thảo luận và điều chỉnh theo ý kiến khách hàng.', image: '' },
              { title: 'BƯỚC 3: THIẾT KẾ KỸ THUẬT', icon: '/images/Untitled-3.png', content: 'Hoàn thiện hồ sơ thiết kế kỹ thuật chi tiết, bản vẽ thi công, thuyết minh kỹ thuật, dự toán chi tiết.', image: '' },
              { title: 'BƯỚC 4: THỦ TỤC PHÁP LÝ', icon: '/images/Untitled-4.png', content: 'Hỗ trợ khách hàng hoàn tất thủ tục xin phép xây dựng, thẩm duyệt thiết kế, các giấy phép cần thiết.', image: '' },
              { title: 'BƯỚC 5: CHUẨN BỊ THI CÔNG', icon: '/images/Untitled-5.png', content: 'Chuẩn bị vật tư, thiết bị, nhân lực. Lập kế hoạch thi công chi tiết, an toàn lao động.', image: '' },
              { title: 'BƯỚC 6: THI CÔNG THỰC HIỆN', icon: '/images/Untitled-6.png', content: 'Thi công theo đúng thiết kế và tiêu chuẩn kỹ thuật. Giám sát chất lượng, tiến độ hàng ngày.', image: '' },
              { title: 'BƯỚC 7: NGHIỆM THU & BÀN GIAO', icon: '/images/Untitled-7.png', content: 'Kiểm tra nghiệm thu từng hạng mục, hoàn thiện công trình, bàn giao hồ sơ kỹ thuật cho khách hàng.', image: '' },
              { title: 'BƯỚC 8: BẢO HÀNH & BẢO TRÌ', icon: '/images/Untitled-8.png', content: 'Bảo hành công trình theo cam kết, hỗ trợ bảo trì định kỳ, tư vấn sử dụng và vận hành.', image: '' }
            ].map((s, idx) => (
              <div key={idx} className={`process-step ${step === idx + 1 ? 'active' : ''}`}>
                <div className={`step-wrapper ${idx % 2 === 0 ? 'left' : 'right'}`}>
                  <button onClick={() => setStep(idx + 1)} className="step-button">
                    <img src={s.icon} alt={s.title} className="step-icon" />
                    <span className="step-title">{s.title}</span>
                  </button>
                  <div className="timeline-dot"></div>
                </div>
                {step === idx + 1 && (
                  <div className="step-detail">
                    <div className="step-detail-content">
                      <h3>{s.title}</h3>
                      <p>{s.content}</p>
                    </div>
                    {s.image && (
                      <div className="step-detail-image">
                        <img src={s.image} alt={s.title} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
      case 'du-an': return renderProjects();
      default: return null;
    }
  };

  return (
    <main className="arch-main">
      {/* Hero Banner */}
      <div className="hero-banner">
        <img src="/images/Rectangle10.png" alt="Architecture & Construction Banner" className="hero-image" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>KIẾN TRÚC & XÂY DỰNG</h1>
          <p>Thiết kế kiến trúc sáng tạo và thi công chuyên nghiệp. Từ nhà ở dân dụng đến các công trình thương mại, công nghiệp quy mô lớn.</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tab-navigation">
        <button 
          onClick={() => setActiveTab('gioi-thieu')}
          className={`tab-btn ${activeTab === 'gioi-thieu' ? 'active' : ''}`}
        >
          GIỚI THIỆU
        </button>
        <button 
          onClick={() => setActiveTab('quy-trinh')}
          className={`tab-btn ${activeTab === 'quy-trinh' ? 'active' : ''}`}
        >
          QUY TRÌNH
        </button>
        <button 
          onClick={() => setActiveTab('du-an')}
          className={`tab-btn ${activeTab === 'du-an' ? 'active' : ''}`}
        >
          DỰ ÁN TIÊU BIỂU
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>

      {/* Project Modal */}
      {isModalOpen && selectedProject && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            <img src={selectedProject.image} alt={selectedProject.title} className="modal-image" />
            <div className="modal-body">
              <h2>{selectedProject.title}</h2>
              <p>{selectedProject.description}</p>
              <div className="modal-tech">
                {selectedProject.technologies.map((tech, idx) => (
                  <span key={idx} className="tech-tag">{tech}</span>
                ))}
              </div>
              {selectedProject.client && (
                <div className="project-info">
                  <p><strong>Khách hàng:</strong> {selectedProject.client}</p>
                </div>
              )}
              {selectedProject.views > 0 && (
                <div className="project-stats">
                  <p>👁️ {selectedProject.views} lượt xem</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Subscription Section */}
      <SubscriptionSection />
    </main>
  );
};

export default ArchitecturalConstruction; 